/**
 * Discussion -- what people said about a record, and what the system recorded.
 *
 * ED, 2026-10-28, from the source product's Quote Response screen: "Build the comments and
 * discussion section too." Comments on top: a thread, oldest first, with who and when; a box to
 * add one, Ctrl+Enter to post; "Comments are hidden when generating PDFs", which is a fact of the
 * renderer and not a setting. The Audit Trail folds below it: the same grid the record's History
 * tab draws, scoped to this object, closed by default because a trail grows without bound.
 *
 * KEYED ON THE LOGICAL OBJECT, like the comments service: a conversation about a quote is about
 * the quote, not revision 2 of it. Anyone who can see the record can speak; only the author can
 * withdraw, and a withdrawn comment keeps its place and says so.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Box, Button, CircularProgress, IconButton, TextField, Typography} from "@mui/material";
import {ExpandLess, ExpandMore, SendOutlined} from "@mui/icons-material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey5, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";
import enterpriseObjectClassFactory from "../../eoec/EnterpriseObjectClassFactory";
import ObjectAuditHistoryTableApplet from "../../eoec/applet/impl/Foundation/ObjectAuditHistoryTableApplet";

type Comment = {USID: string; ParentCommentUSID?: string | null; Body?: string | null;
                AuthorUSID?: string | null; AuthorName?: string | null;
                CreatedDatetime?: string | null; Withdrawn?: boolean; WithdrawnDatetime?: string | null};

const listOf = <T,>(held: T[] | T | undefined | null): T[] =>
    Array.isArray(held) ? held : (held ? [held] : []);

function whenAt(value?: string | null): string {
    if (!value) return "";
    const date = new Date(value.endsWith("Z") || /[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export default function DiscussionPane({objectUSID}: {objectUSID: string}) {
    const appContext: any = useContext(AppContext);
    const api = useMemo(() => makeCresAxios(appContext?.sessionContext), [appContext?.sessionContext]);

    const [comments, setComments] = useState<Comment[] | null>(null);
    const [count, setCount] = useState(0);
    const [caller, setCaller] = useState<string | null>(null);
    const [draft, setDraft] = useState("");
    const [replyTo, setReplyTo] = useState<Comment | null>(null);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    const [commentsOpen, setCommentsOpen] = useState(true);
    const [trailOpen, setTrailOpen] = useState(false);

    const load = useCallback(() => {
        if (!objectUSID) return;
        api.get("t1/v1/object_comments", {params: {ObjectUSID: objectUSID}})
            .then((r: any) => {
                const message = r?.data?.PlatformMessage ?? {};
                setComments(listOf<Comment>(message?.ListOfObjectComment?.ObjectComment));
                setCount(Number(message?.Count ?? 0));
                setCaller(message?.CallerUSID ?? null);
                setProblem(null);
            })
            .catch((failure: any) => {
                setComments([]);
                setProblem(failure?.response?.data?.Cause ?? "The discussion could not be read.");
            });
    }, [api, objectUSID]);

    useEffect(() => { load(); }, [load]);

    const post = async () => {
        const body = draft.trim();
        if (!body || busy) return;
        setBusy(true); setProblem(null);
        try {
            await api.post("t1/v1/object_comments", {
                ObjectUSID: objectUSID, Body: body,
                ParentCommentUSID: replyTo?.USID ?? undefined,
            });
            setDraft(""); setReplyTo(null);
            load();
        } catch (failure: any) {
            setProblem(failure?.response?.data?.Cause ?? "The comment could not be posted.");
        } finally {
            setBusy(false);
        }
    };

    const withdraw = async (one: Comment) => {
        setBusy(true); setProblem(null);
        try {
            await api.delete("t1/v1/object_comments", {params: {USID: one.USID}});
            load();
        } catch (failure: any) {
            setProblem(failure?.response?.data?.Cause ?? "The comment could not be withdrawn.");
        } finally {
            setBusy(false);
        }
    };

    // THE AUDIT TRAIL, the History tab's own grid, scoped to this object before it mounts: an
    // unscoped mount would show every audit row in the realm.
    const auditEO: any = useMemo(() => enterpriseObjectClassFactory("ObjectAuditHistory"), []);
    useMemo(() => {
        auditEO?.getEnterpriseComponent("ObjectAuditHistory")?.setScope?.("ObjectUSID", objectUSID);
    }, [auditEO, objectUSID]);

    const byParent = useMemo(() => {
        const map: Record<string, Comment[]> = {};
        (comments ?? []).forEach(one => {
            const key = one.ParentCommentUSID ?? "";
            (map[key] = map[key] ?? []).push(one);
        });
        return map;
    }, [comments]);

    const renderComment = (one: Comment, depth: number): React.ReactNode => (
        <Box key={one.USID} sx={{marginLeft: `${depth * 28}px`, padding: "8px 0",
                                 borderTop: depth === 0 ? `1px solid ${Grey2}` : "none"}}>
            <Box sx={{display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap"}}>
                <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey17}}>
                    {one.AuthorName ?? "Someone"}
                </Typography>
                <Typography sx={{fontSize: 11.5, color: Grey6}}>{whenAt(one.CreatedDatetime)}</Typography>
                <Box sx={{flex: 1}}/>
                {!one.Withdrawn && (
                    <Typography sx={{fontSize: 11.5, color: Blue3, cursor: "pointer"}}
                                onClick={() => setReplyTo(one)}>Reply</Typography>
                )}
                {!one.Withdrawn && caller && one.AuthorUSID === caller && (
                    <Typography sx={{fontSize: 11.5, color: Grey6, cursor: "pointer"}}
                                onClick={() => withdraw(one)}>Withdraw</Typography>
                )}
            </Box>
            {one.Withdrawn
                ? <Typography sx={{fontSize: 13, color: Grey6, fontStyle: "italic"}}>Withdrawn.</Typography>
                : <Typography sx={{fontSize: 13, color: Grey17, whiteSpace: "pre-wrap", lineHeight: 1.5}}>{one.Body}</Typography>}
            {(byParent[one.USID] ?? []).map(child => renderComment(child, depth + 1))}
        </Box>
    );

    const roots = byParent[""] ?? [];

    return (
        <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "8px", padding: "12px 16px"}}>
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, paddingBottom: "8px"}}>Discussion</Typography>

            {/* COMMENTS */}
            <Box sx={{display: "flex", alignItems: "center", gap: "6px", cursor: "pointer"}}
                 onClick={() => setCommentsOpen(open => !open)}>
                <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey17}}>
                    Comments{comments ? ` (${count})` : ""}
                </Typography>
                <Box sx={{flex: 1}}/>
                {commentsOpen ? <ExpandLess sx={{fontSize: 18, color: Grey6}}/> : <ExpandMore sx={{fontSize: 18, color: Grey6}}/>}
            </Box>
            {commentsOpen && (
                <Box sx={{paddingTop: "6px"}}>
                    {comments === null && !problem && (
                        <Box sx={{display: "flex", gap: "8px", alignItems: "center", padding: "6px 0"}}>
                            <CircularProgress size={14}/>
                            <Typography sx={{fontSize: 12.5, color: Grey7}}>Reading the discussion…</Typography>
                        </Box>
                    )}
                    {comments && roots.length === 0 && (
                        <Typography sx={{fontSize: 12.5, color: Grey6, padding: "4px 0"}}>
                            No comments yet. Be the first to add one.
                        </Typography>
                    )}
                    {roots.map(one => renderComment(one, 0))}

                    <Box sx={{marginTop: "10px"}}>
                        {replyTo && (
                            <Typography sx={{fontSize: 11.5, color: Grey7, paddingBottom: "4px"}}>
                                Replying to {replyTo.AuthorName ?? "a comment"}.{" "}
                                <span style={{color: Blue3, cursor: "pointer"}} onClick={() => setReplyTo(null)}>Cancel</span>
                            </Typography>
                        )}
                        <Box sx={{display: "flex", gap: "8px", alignItems: "flex-end"}}>
                            <TextField size="small" fullWidth multiline minRows={2} maxRows={8}
                                       placeholder="Add a comment…" value={draft}
                                       onChange={event => setDraft(event.target.value)}
                                       onKeyDown={event => {
                                           if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                                               event.preventDefault(); post();
                                           }
                                       }}/>
                            <IconButton size="small" aria-label="Post this comment"
                                        disabled={busy || draft.trim() === ""} onClick={post}
                                        sx={{color: "white", background: Blue3, borderRadius: "6px",
                                             "&:hover": {background: Blue3}, "&.Mui-disabled": {background: "#C9D4E1", color: "white"}}}>
                                <SendOutlined sx={{fontSize: 18}}/>
                            </IconButton>
                        </Box>
                        <Typography sx={{fontSize: 11, color: Grey5, paddingTop: "4px"}}>
                            Tip: Ctrl+Enter to post · Comments are internal and never reach the customer PDF.
                        </Typography>
                        {problem && <Typography sx={{fontSize: 12.5, color: "#B3261E", paddingTop: "4px"}}>{problem}</Typography>}
                    </Box>
                </Box>
            )}

            {/* AUDIT TRAIL */}
            <Box sx={{display: "flex", alignItems: "center", gap: "6px", cursor: "pointer",
                      marginTop: "12px", paddingTop: "10px", borderTop: `1px solid ${Grey2}`}}
                 onClick={() => setTrailOpen(open => !open)}>
                <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey17}}>Audit Trail</Typography>
                <Box sx={{flex: 1}}/>
                {trailOpen ? <ExpandLess sx={{fontSize: 18, color: Grey6}}/> : <ExpandMore sx={{fontSize: 18, color: Grey6}}/>}
            </Box>
            {trailOpen && (
                <Box sx={{paddingTop: "8px"}}>
                    <ObjectAuditHistoryTableApplet enterpriseComponent={auditEO?.getEnterpriseComponent("ObjectAuditHistory")}/>
                </Box>
            )}
        </Box>
    );
}
